import { useState, useEffect } from "react";
import { Table, Form, InputGroup, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { listarUsuariosBac, crearUsuarioBac, editarUsuarioBac, eliminarUsuarioBac } from "../api/bacAuthAPI";
import { useBacAuth } from "../../context/BacAuthContext";
import EditarUsuarioModal from "../modal/EditarUsuarioModal";

const VACIO = { id: null, nombre: "", correo: "", cedula: "", password: "", rol: "gestor" };
const TH = { padding: "11px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 12 };
const TD = { padding: "10px 12px", verticalAlign: "middle", fontSize: 13 };
const REGISTROS_PAG = 10;

const ESTADO_BADGE = {
  pendiente: { bg: "#faf0dc", color: "#854F0B", label: "⏳ Pendiente" },
  activo:    { bg: "#e4f2e8", color: "#2d6a4f", label: "✓ Activo" },
  inactivo:  { bg: "#fbe4e2", color: "#b3261e", label: "✕ Inactivo" },
};

export default function PageUsuariosBac() {
  const { usuario: yo } = useBacAuth();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [usuarioEditar, setUsuarioEditar] = useState(null);

  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [pagina, setPagina] = useState(1);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await listarUsuariosBac();
      setLista(res.data || []);
    } catch {
      Swal.fire("Error", "No se pudo cargar la lista de usuarios", "error");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);
  useEffect(() => { setPagina(1); }, [busqueda, filtroRol, filtroEstado]);

  // Crear queda en el formulario en línea (rápido); editar abre un
  // modal aparte, más cómodo para cambiar varios campos a la vez.
  const guardar = async () => {
    if (!form.nombre || !form.cedula || !form.correo || !form.password) {
      Swal.fire({ icon: "warning", title: "Completa nombre, cédula, correo y contraseña", timer: 2000, showConfirmButton: false });
      return;
    }
    setGuardando(true);
    try {
      await crearUsuarioBac({ nombre: form.nombre, correo: form.correo, cedula: form.cedula, password: form.password, rol: form.rol });
      setForm(VACIO);
      await cargar();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo guardar", "error");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (u, estado) => {
    try {
      await editarUsuarioBac(u.id, { estado });
      await cargar();
    } catch {
      Swal.fire("Error", "No se pudo cambiar el estado", "error");
    }
  };

  const eliminar = async (u) => {
    const confirmado = await Swal.fire({
      icon: "warning", title: `¿Eliminar a ${u.nombre}?`, showCancelButton: true,
      confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", confirmButtonColor: "#b3261e",
    });
    if (!confirmado.isConfirmed) return;
    try {
      await eliminarUsuarioBac(u.id);
      await cargar();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "No se pudo eliminar", "error");
    }
  };

  const pendientes = lista.filter(u => u.estado === "pendiente").length;

  const filtrados = lista.filter(u => {
    const q = busqueda.toLowerCase();
    const matchQ = !busqueda || u.nombre?.toLowerCase().includes(q) || u.correo?.toLowerCase().includes(q) || u.cedula?.includes(q);
    const matchRol = !filtroRol || u.rol === filtroRol;
    const matchEstado = !filtroEstado || u.estado === filtroEstado;
    return matchQ && matchRol && matchEstado;
  });

  const totalPags = Math.ceil(filtrados.length / REGISTROS_PAG);
  const paginados = filtrados.slice((pagina - 1) * REGISTROS_PAG, pagina * REGISTROS_PAG);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      <div className="bac-hero" style={{
        background: "linear-gradient(135deg, #16342a 0%, #1f4a3a 60%, #2d6a4f 100%)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 20, color: "#fff",
      }}>
        <div className="bac-hero-eyebrow" style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
          👤 Administración
        </div>
        <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.15rem" }}>Usuarios de BAC Sarampión</h5>
        {pendientes > 0 && (
          <button onClick={() => setFiltroEstado("pendiente")} style={{
            marginTop: 12, background: "rgba(255,193,7,.25)", border: "none", borderRadius: 10,
            padding: "6px 14px", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
          }}>
            ⏳ {pendientes} usuario(s) esperando aprobación — clic para ver
          </button>
        )}
      </div>

      {/* Formulario — solo crear (editar se hace en el modal aparte) */}
      <div className="p-3 rounded mb-3" style={{ background: "#f5f6f0" }}>
        <div className="fw-bold mb-2" style={{ fontSize: 13 }}>Nuevo usuario (queda activo de inmediato)</div>
        <div className="row g-2">
          <div className="col-12 col-md-4">
            <Form.Control size="sm" placeholder="Nombre completo" value={form.nombre}
              onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} />
          </div>
          <div className="col-6 col-md-2">
            <Form.Control size="sm" placeholder="N° de cédula" value={form.cedula}
              onChange={e => setForm(p => ({ ...p, cedula: e.target.value }))} />
          </div>
          <div className="col-6 col-md-2">
            <Form.Select size="sm" value={form.rol} onChange={e => setForm(p => ({ ...p, rol: e.target.value }))}>
              <option value="gestor">Gestor</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </div>
          <div className="col-12 col-md-4">
            <Form.Control size="sm" type="email" placeholder="Correo electrónico (login)" value={form.correo}
              onChange={e => setForm(p => ({ ...p, correo: e.target.value }))} />
          </div>
          <div className="col-12 col-md-8">
            <Form.Control size="sm" type="password" placeholder="Contraseña"
              value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          </div>
          <div className="col-12 col-md-4">
            <Button size="sm" variant="success" onClick={guardar} disabled={guardando} className="w-100">
              {guardando ? "Guardando..." : "Crear usuario"}
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <InputGroup style={{ maxWidth: 260, flex: 1 }}>
          <InputGroup.Text style={{ background: "#fff", border: "1.5px solid #dee2e6", borderRight: "none" }}>🔍</InputGroup.Text>
          <Form.Control
            placeholder="Nombre, correo o cédula..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ fontSize: 13, border: "1.5px solid #dee2e6", borderLeft: "none" }}
          />
        </InputGroup>
        <Form.Select value={filtroRol} onChange={e => setFiltroRol(e.target.value)} style={{ maxWidth: 150, fontSize: 13 }}>
          <option value="">Todos los roles</option>
          <option value="admin">Admin</option>
          <option value="gestor">Gestor</option>
        </Form.Select>
        <Form.Select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ maxWidth: 170, fontSize: 13 }}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="activo">Activo</option>
          <option value="inactivo">Inactivo</option>
        </Form.Select>
      </div>

      {cargando ? <p className="text-muted">Cargando...</p> : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e9ecef" }}>
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ marginBottom: 0 }}>
              <thead style={{ background: "#f8faf8" }}>
                <tr>
                  <th style={TH}>#</th>
                  <th style={TH}>Nombre</th>
                  <th style={TH}>Cédula</th>
                  <th style={TH}>Correo</th>
                  <th style={TH}>Rol</th>
                  <th style={TH}>Estado</th>
                  <th style={TH}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((u, i) => (
                  <tr key={u.id}>
                    <td style={{ ...TD, color: "#8a94a6" }}>{(pagina - 1) * REGISTROS_PAG + i + 1}</td>
                    <td style={TD}>{u.nombre}</td>
                    <td style={{ ...TD, fontFamily: "monospace" }}>{u.cedula}</td>
                    <td style={TD}>{u.correo}</td>
                    <td style={TD}>
                      <span className={`badge ${u.rol === "admin" ? "bg-dark" : "bg-secondary"}`}>{u.rol}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ background: ESTADO_BADGE[u.estado]?.bg, color: ESTADO_BADGE[u.estado]?.color, padding: "3px 9px", borderRadius: 6, fontSize: 11.5, fontWeight: 700 }}>
                        {ESTADO_BADGE[u.estado]?.label || u.estado}
                      </span>
                    </td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>
                      {u.estado === "pendiente" && (
                        <button onClick={() => cambiarEstado(u, "activo")} className="btn btn-sm btn-success py-0 px-2 me-1" style={{ fontSize: 11 }}>Aprobar</button>
                      )}
                      {u.estado === "activo" && (
                        <button onClick={() => cambiarEstado(u, "inactivo")} className="btn btn-sm btn-outline-secondary py-0 px-2 me-1" style={{ fontSize: 11 }}>Desactivar</button>
                      )}
                      {u.estado === "inactivo" && (
                        <button onClick={() => cambiarEstado(u, "activo")} className="btn btn-sm btn-outline-success py-0 px-2 me-1" style={{ fontSize: 11 }}>Reactivar</button>
                      )}
                      <button onClick={() => setUsuarioEditar(u)} className="btn btn-sm btn-link p-0 me-2" style={{ fontSize: 13 }}>✏️</button>
                      {u.correo !== yo?.correo && (
                        <button onClick={() => eliminar(u)} className="btn btn-sm btn-link p-0 text-danger" style={{ fontSize: 13 }}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!paginados.length && (
                  <tr><td colSpan={7} style={{ ...TD, textAlign: "center", color: "#8a94a6", fontStyle: "italic" }}>Sin usuarios</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div style={{ padding: "10px 20px", background: "#f8faf8", borderTop: "1px solid #e9ecef", fontSize: 12, color: "#6c757d", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span>Mostrando <strong>{paginados.length}</strong> de <strong>{filtrados.length}</strong> · Total: <strong>{lista.length}</strong></span>
            {totalPags > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>◀</button>
                <span style={{ fontWeight: 700 }}>{pagina}/{totalPags}</span>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
                  style={{ border: "1px solid #dee2e6", background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12 }}>▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      <EditarUsuarioModal
        show={!!usuarioEditar}
        usuario={usuarioEditar}
        onClose={() => setUsuarioEditar(null)}
        onGuardado={cargar}
      />
    </div>
  );
}